/**
 * wagmi + chain configuration
 *
 * Environment-driven chain selection via VITE_CHAIN_ID.
 * Supports 0G testnet (16600), 0G mainnet (TBD), and Base (8453) for dev.
 * VITE_RPC_URL overrides the active chain's transport (for local Anvil fork).
 */

import { defineChain } from "viem";
import { http, createConfig } from "wagmi";

/**
 * 0G Testnet chain definition
 */
export const ogTestnet = defineChain({
	id: 16600,
	name: "0G Newton Testnet",
	nativeCurrency: {
		name: "A0GI",
		symbol: "A0GI",
		decimals: 18,
	},
	rpcUrls: {
		default: {
			http: ["https://evmrpc-testnet.0g.ai"],
		},
	},
	blockExplorers: {
		default: {
			name: "0G Explorer",
			url: "https://chainscan-newton.0g.ai",
		},
	},
	testnet: true,
});

/**
 * Base chain definition (for local development / fork testing)
 */
export const base = defineChain({
	id: 8453,
	name: "Base",
	nativeCurrency: {
		name: "Ether",
		symbol: "ETH",
		decimals: 18,
	},
	rpcUrls: {
		default: {
			http: ["https://mainnet.base.org"],
		},
	},
	blockExplorers: {
		default: {
			name: "Basescan",
			url: "https://basescan.org",
		},
	},
	testnet: false,
});

/**
 * All supported chains for wagmi config
 */
const supportedChains = [ogTestnet, base] as const;

/**
 * Get the active chain from VITE_CHAIN_ID env var. Defaults to 0G testnet.
 */
export function getActiveChain(): (typeof supportedChains)[number] {
	const envChainId = import.meta.env.VITE_CHAIN_ID;
	const chainId = envChainId ? Number(envChainId) : 16600;
	switch (chainId) {
		case 8453:
			return base;
		default:
			return ogTestnet;
	}
}

/**
 * Get the native currency symbol for the active chain.
 * Returns "A0GI" on testnet, "ETH" on Base, etc.
 */
export function getNativeCurrencySymbol(): string {
	return getActiveChain().nativeCurrency.symbol;
}

/**
 * Get the block explorer URL for the active chain.
 */
export function getBlockExplorerUrl(): string {
	return getActiveChain().blockExplorers.default.url;
}

/** RPC URL override from env — points wagmi reads to local Anvil fork */
const rpcOverride = import.meta.env.VITE_RPC_URL;

/**
 * wagmi config with all supported chains.
 * When VITE_RPC_URL is set, the active chain's transport points to local Anvil
 * so that wagmi reads are consistent with MetaMask writes.
 */
export const wagmiConfig = createConfig({
	chains: supportedChains,
	transports: {
		[ogTestnet.id]:
			rpcOverride && getActiveChain().id === ogTestnet.id ? http(rpcOverride) : http(),
		[base.id]: rpcOverride && getActiveChain().id === base.id ? http(rpcOverride) : http(),
	},
});
